import { Typography } from '@modulor/react';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import type { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { getClickStreamEvent, getEventAnalytic, useGetClickStreamCashierContext } from 'services/clickstream';
import { useAppSelector } from 'store/hooks';
import { paymentFlowSelector, userBlockedSelector } from 'store/slices/global';
import { submitRedirectBlankSelector } from 'store/slices/submitForm';
import {
  cryptoCurrenciesSelector,
  currentGroupSelector,
  fiatCurrenciesSelector,
  onRampCurrenciesSelector,
  selectedCurrencySelector,
} from 'store/slices/wallet';
import { getWidgetLocation } from 'utils';
import { useGetSessionId } from 'utils/session';

import { CryptoSelectBlock } from './CryptoSelectBlock';
import type { SelectValue } from './Select/CurrencySelect';
import CurrencySelect from './Select/CurrencySelect';
import { PaymentType } from './types';
import { useCurrencyFlow } from './useCurrencyFlow';
import { useCurrencyNavigation } from './useCurrencyNavigation';

const clickstream = getEventAnalytic();

export const CurrencySelectContainer: FC = () => {
  const location = useLocation();
  const selectedCurrency = useAppSelector(selectedCurrencySelector);
  const currentGroup = useAppSelector(currentGroupSelector);
  const fiatCurrencies = useAppSelector(fiatCurrenciesSelector);
  const cryptoCurrencies = useAppSelector(cryptoCurrenciesSelector);
  const onRampCurrencies = useAppSelector(onRampCurrenciesSelector);
  const userBlocked = useAppSelector(userBlockedSelector);
  const isRedirectBlank = useAppSelector(submitRedirectBlankSelector);
  const { translate } = useSkeletonTranslation();
  const sessionId = useGetSessionId();
  const flowType = useAppSelector(paymentFlowSelector);
  const cashierContext = useGetClickStreamCashierContext(flowType);
  const event = getClickStreamEvent.pw_currency_click(sessionId);
  const networkEvent = getClickStreamEvent.pw_network_click(sessionId);
  const { openCurrencySelector, openNetworkSelector } = useCurrencyNavigation();
  const { activeTab, currencyFlow } = useCurrencyFlow(fiatCurrencies, cryptoCurrencies, onRampCurrencies);
  const fiatCurrencyCount = fiatCurrencies.length;
  const cryptoCurrencyCount = cryptoCurrencies.length;
  const onRampCurrencyCount = onRampCurrencies.length;

  const handleFiatClick = () => {
    clickstream.push(event, [cashierContext]);
    openCurrencySelector(PaymentType.FIAT);
  };
  const handleCryptoClick = () => {
    clickstream.push(event, [cashierContext]);
    openCurrencySelector(PaymentType.CRYPTO);
  };
  const handleOnRampClick = () => {
    clickstream.push(event, [cashierContext]);
    openCurrencySelector(PaymentType.ON_RAMP);
  };
  const handleNetworkClick = () => {
    clickstream.push(networkEvent, [cashierContext]);
    openNetworkSelector();
  };

  const { isStatusPage } = getWidgetLocation(location.pathname);

  const isCryptoFlow = currencyFlow === 'crypto' || activeTab === PaymentType.CRYPTO;
  const isFiatFlow = currencyFlow === 'fiat' || activeTab === PaymentType.FIAT;
  const isOnRampFlow = activeTab === PaymentType.ON_RAMP;

  const currencyValue: SelectValue | null = selectedCurrency
    ? {
        id: selectedCurrency.id,
        title: selectedCurrency.alpha3,
        icon: selectedCurrency.iconUrl,
      }
    : null;

  const networkValue: SelectValue | null = currentGroup
    ? {
        id: currentGroup.id,
        title: translate(currentGroup.displayName),
        icon: currentGroup.logoUri,
      }
    : null;

  if (isStatusPage || isRedirectBlank) return null;

  if (isCryptoFlow) {
    return (
      <>
        <Typography variant="subhead-regular" tag="p" color="text-body" data-testid="currency-select-subtitle">
          {translate(`PH.SELECT.CURRENCY.SUBTITLE.${flowType.toUpperCase()}`)}
        </Typography>
        <CryptoSelectBlock
          selectedCurrency={currencyValue}
          onCurrencyClick={handleCryptoClick}
          onNetworkClick={handleNetworkClick}
          networkValue={networkValue}
          isOnlyOneCurrency={cryptoCurrencyCount === 1}
          userBlocked={userBlocked}
        />
      </>
    );
  }

  if (isFiatFlow && fiatCurrencyCount > 1) {
    return (
      <CurrencySelect
        selectedCurrency={currencyValue}
        fullWidth
        label={translate('PH.SELECT.CURRENCY.LABEL')}
        onClick={handleFiatClick}
        dataTestId="currency-select"
      />
    );
  }

  if (isOnRampFlow) {
    return (
      <CurrencySelect
        selectedCurrency={currencyValue}
        fullWidth
        label={translate('PH.SELECT.BUY.CURRENCY.LABEL')}
        onClick={handleOnRampClick}
        dataTestId="currency-select"
        hideRigthIcon={onRampCurrencyCount === 1}
      />
    );
  }

  return null;
};
